import {BigCalendarEvent, BigCalendar} from 'platform/big-calendar';
import {DataStatus} from 'platform/components';

import {useEditTaskMutation} from '@dms/api/core';
import {useUpdateTestDriveMutation} from '@dms/api/sales';
import {openCreateTaskFormDialog, openEditTaskFormDialog} from '@dms/features/tasks';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {getCreateTaskDefaultValues} from '../../../../utils/getCreateTaskDefaultValues';
import {useBigCalendarControl} from './hooks/useBigCalendarControl';
import {getBigCalendarDataFromGrid} from './utils/getBigCalendarDataFromGrid';
import {getBigCalendarDataFromTestDriveGrid} from './utils/getBigCalendarDataFromTestDriveGrid';
import {getTaskFromBigCalendarEvent} from './utils/getTaskFromBigCalendarEvent';
import {isTestDriveEvent} from './utils/isTestDriveEvent';

export function TaskCalendar() {
  const navigate = useNavigate();

  const [canCreateTask, canEditTestDrive] = usePermissions({
    permissionKeys: ['createTask', 'editTestDrive'],
  });

  const control = useBigCalendarControl();

  const [editTask] = useEditTaskMutation();
  const [updateTestDrive] = useUpdateTestDriveMutation();

  const taskEvents = getBigCalendarDataFromGrid(control.tasks, control.selectedUsers);
  const testDriveEvents = getBigCalendarDataFromTestDriveGrid(
    control.testDrives,
    control.selectedUsers
  );
  const allEvents = [...(taskEvents ?? []), ...(testDriveEvents ?? [])];

  const onEventDragMove = async (event: BigCalendarEvent, start: Date, end: Date) => {
    if (isTestDriveEvent(event)) {
      if (!canEditTestDrive || !event.testDriveData) {
        return;
      }

      await updateTestDrive({
        testDriveId: event.id,
        updateTestDriveRequestBody: {
          userId: event.testDriveData.userId,
          state: event.testDriveData.state,
          note: event.testDriveData.note,
          scheduledAt: start.toISOString(),
          returnedAt: event.testDriveData.hasReturnedAt ? end.toISOString() : null,
        },
      })
        .unwrap()
        .catch(handleApiError);
      return;
    }

    const editTaskRequestBody = getTaskFromBigCalendarEvent({
      event,
      newStart: start,
      newEnd: end,
      dataGridData: control.tasks,
    });

    if (!editTaskRequestBody) {
      return;
    }

    await editTask({
      id: event.id,
      editTaskRequestBody,
    }).unwrap();
  };

  const onDateClick = (date: Date) => {
    if (!canCreateTask) {
      return null;
    }
    openCreateTaskFormDialog({
      'data-testid': testIds.taskManager.taskCalendar('createTaskDialog'),
      values: getCreateTaskDefaultValues(date),
    });
  };

  const onEventClick = (event: BigCalendarEvent) => {
    if (isTestDriveEvent(event)) {
      if (event.businessCaseId) {
        const path = composePath(businessCaseRoutes.testDrive, {
          params: {id: event.businessCaseId},
          queryParams: {editTestDrive: event.id},
        });
        navigate(path);
      }
      return;
    }

    openEditTaskFormDialog({
      id: event.id,
      'data-testid': testIds.taskManager.taskCalendar('editTaskDialog'),
    });
  };

  return (
    <DataStatus
      data={control.currentUser ? control.selectedUsers : null}
      isLoading={control.isLoading}
      isError={control.isError}
      minHeight={150}
    >
      {({data}) => (
        <BigCalendar
          data-testid={testIds.taskManager.taskCalendar('tasksCalendar')}
          defaultView="week"
          events={allEvents}
          users={control.users}
          currentUser={control.currentUser!}
          selectedUsers={data}
          currentDate={control.selectedDate}
          onNavigate={control.onNavigate}
          onDateClick={onDateClick}
          onEventClick={onEventClick}
          onEventDragMove={onEventDragMove}
          onUserControlChange={control.onUserControlChange}
        />
      )}
    </DataStatus>
  );
}
